import { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import TypingIndicator from "./TypingIndicator";
import { sendChatMessage, clearConversation } from "../../services/apiCalls";

const DRAWER_WIDTH = 550;

const ChatPanel = ({ open, onClose, subjectId }) => {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { t } = useTranslation("chat");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset conversation when panel opens with a new subject
  useEffect(() => {
    if (open && subjectId) {
      // Clear previous conversation when opening for a different subject
      setMessages([]);
      setConversationId(null);
    }
  }, [open, subjectId]);

  const handleSend = async (message) => {
    if (!subjectId) {
      console.error("No subject ID provided");
      return;
    }

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      message,
      isUser: true,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await sendChatMessage(message, subjectId, conversationId);

      // Save conversation ID for follow-up messages
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Add bot response to chat with animation flag
      const botMessage = {
        id: Date.now() + 1,
        message: response.response,
        isUser: false,
        agentUsed: response.agent_used,
        timestamp: response.timestamp,
        animate: true,
      };
      setMessages((prev) => {
        // Remove animation flag from previous messages
        const updatedPrev = prev.map((msg) => ({ ...msg, animate: false }));
        return [...updatedPrev, botMessage];
      });
    } catch (error) {
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        message: t("errorMessage"),
        isUser: false,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = async () => {
    if (conversationId) {
      await clearConversation(conversationId);
    }
    setMessages([]);
    setConversationId(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          maxWidth: "100vw",
          height: "65vh",
          top: "auto",
          bottom: 0,
          borderTopLeftRadius: 12,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            backgroundColor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">{t("title")}</Typography>
          <Box>
            <IconButton
              onClick={handleClearChat}
              sx={{ color: "white" }}
              title={t("clearChat")}
            >
              <DeleteOutlineIcon />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider />

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            backgroundColor: "#fafafa",
          }}
        >
          {messages.length === 0 && !loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2" align="center">
                {t("welcomeMessage")}
              </Typography>
            </Box>
          ) : (
            <>
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg.message}
                  isUser={msg.isUser}
                  timestamp={msg.timestamp}
                  animate={msg.animate}
                />
              ))}
              {loading && <TypingIndicator />}
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <ChatInput onSend={handleSend} disabled={loading} />
      </Box>
    </Drawer>
  );
};

export default ChatPanel;
